import { stashDataSource } from '@/features/dataSources/dataSourcesSlice';
import { useWorkspace } from '@/features/workspaces/hooks';
import { DataSource, DataSourceFileConfig, DataSourceIDEnum, Header } from '@/models/dataSource';
import { EStatus } from '@/models/fileUpload';
import { useAppDispatch, useAppSelector } from '@/reducers';
import { useGetMappingSuggestionsMutation, useSaveDataSourceMutation } from '@/services/dataSources';
import { FormikProps } from 'formik';
import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnItem } from '../../../../types';
import { ColumnMapEditor, getHeadersFromColumnMap } from '../../../commons/ColumnMapEditor';
import { applyMappingSuggestions, MAPPING_SUGGESTIONS_TIMEOUT_MS, toColumnMapFileKey } from '../mappingSuggestions';

type FileDataSourceColumnMap = {
  onNextStep?: VoidFunction;
  onPrevStep?: VoidFunction;
  onCancel?: VoidFunction;
  isToCheckUploadSuccess?: boolean;
  requiredMapping?: { traitIds: Array<string | number>; tooltip: string };
  autoAudienceTraitId?: string;
};

const MAX_ROWS = 4;

type SuggestionRequestState = {
  key: string;
  pending: boolean;
  headers?: Header[];
  sources?: string[];
};

export const FileDataSourceColumnMapStep: FC<FileDataSourceColumnMap> = ({
  onNextStep,
  onPrevStep,
  isToCheckUploadSuccess = false,
  requiredMapping,
  autoAudienceTraitId,
}) => {
  const dispatch = useAppDispatch();
  const workspace = useWorkspace();
  const fileUploadStatus = useAppSelector((state) => state.fileUploading.status);
  const { t } = useTranslation('data_source_edit');

  // fixedCacheKey lets FileDataSourceConfigurationCompletedStep read the saved datasource (with its id)
  const [saveDataSource] = useSaveDataSourceMutation({ fixedCacheKey: 'saveDataSource' });
  const [getMappingSuggestions] = useGetMappingSuggestionsMutation();
  const columnMapFormRef = useRef<FormikProps<ColumnItem[]>>(null);

  const dataSource = useAppSelector((state) => state.dataSources.edit?.dataSource) as Partial<DataSource<DataSourceFileConfig>> | undefined;
  const fileData = useAppSelector((state) => state.fileUploading.fileData);

  const fileHeader: string[] = fileData ? [...fileData.header] : [];
  const previewRows: string[][] = fileData
    ? fileData.isDefaultHeader
      ? [...fileData.preview.slice(0, MAX_ROWS).slice(1)]
      : [...fileData.preview.slice(0, MAX_ROWS)]
    : [];

  const isUploadSuccess = fileUploadStatus?.uploadingStatus === EStatus.SUCCESS;
  const columnMapFileKey = toColumnMapFileKey(workspace.id, fileHeader, previewRows);
  const savedHeaders = dataSource?.configuration?.headers;
  const hasSavedMapping = Boolean(savedHeaders?.length) && dataSource?.configuration?.headersFileKey === columnMapFileKey && columnMapFileKey !== '';
  const suggestionRequestKey = !hasSavedMapping && columnMapFileKey ? columnMapFileKey : '';
  const suggestionPayloadRef = useRef({ workspaceId: workspace.id, headers: fileHeader, samples: previewRows });
  suggestionPayloadRef.current = { workspaceId: workspace.id, headers: fileHeader, samples: previewRows };
  const [suggestionRequest, setSuggestionRequest] = useState<SuggestionRequestState>({ key: '', pending: false });

  useEffect(() => {
    if (!suggestionRequestKey) {
      setSuggestionRequest({ key: '', pending: false });
      return undefined;
    }

    setSuggestionRequest({ key: suggestionRequestKey, pending: true });

    const { workspaceId, headers, samples } = suggestionPayloadRef.current;
    const request = getMappingSuggestions({ workspaceId, headers, samples });
    const timeoutId = window.setTimeout(() => {
      request.abort();
    }, MAPPING_SUGGESTIONS_TIMEOUT_MS);
    let active = true;

    request
      .unwrap()
      .then((suggestions) => {
        if (!active) return;

        const applied = applyMappingSuggestions(suggestions, headers.length);
        if (!applied) {
          setSuggestionRequest({ key: suggestionRequestKey, pending: false });
          return;
        }

        setSuggestionRequest({
          key: suggestionRequestKey,
          pending: false,
          headers: applied.headers,
          sources: applied.sources,
        });
      })
      .catch(() => {
        if (!active) return;
        setSuggestionRequest({ key: suggestionRequestKey, pending: false });
      })
      .finally(() => {
        window.clearTimeout(timeoutId);
      });

    return () => {
      active = false;
      window.clearTimeout(timeoutId);
      request.abort();
    };
  }, [getMappingSuggestions, suggestionRequestKey]);

  const handleConfirm = async (headers: ReturnType<typeof getHeadersFromColumnMap>, options?: { normalizeAddresses?: boolean }) => {
    if (!dataSource?.name || !dataSource.configuration) return;

    dispatch(stashDataSource({ configuration: { ...dataSource.configuration, headers, headersFileKey: columnMapFileKey } }));
    // headersFileKey is wizard-only and must not be sent to the API.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { headersFileKey, ...configurationWithoutFileKey } = dataSource.configuration;
    let finalConfiguration: Record<string, unknown> = { ...configurationWithoutFileKey };

    if (dataSource.type.id === DataSourceIDEnum.SFTP) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { authMethod, ...rest } = finalConfiguration;
      finalConfiguration = rest;
    }

    if (dataSource.type.id === DataSourceIDEnum.CsvFile || dataSource.type.id === DataSourceIDEnum.EncryptedCsvFile) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { filename, ...rest } = finalConfiguration;
      finalConfiguration = rest;
    }

    const response = await saveDataSource({
      workspaceId: workspace.id,
      dataSource: {
        type: dataSource.type.id,
        name: dataSource.name,
        configuration: {
          ...finalConfiguration,
          type: dataSource.configuration.type,
          isDefaultHeader: dataSource.configuration.isDefaultHeader,
          headers,
          url: dataSource.configuration.url || fileUploadStatus?.data?.path,
          ...(options?.normalizeAddresses && { normalizeAddresses: true }),
        },
        ...(autoAudienceTraitId && { autoAudienceTraitId }),
      },
    });

    if (!('error' in response && response.error)) {
      onNextStep?.();
    }
  };

  const handlePrev = () => {
    const columnMap = columnMapFormRef.current?.values;
    if (columnMap && dataSource?.configuration) {
      dispatch(stashDataSource({ configuration: { ...dataSource.configuration, headers: getHeadersFromColumnMap(columnMap), headersFileKey: columnMapFileKey } }));
    }
    onPrevStep?.();
  };

  return (
    <ColumnMapEditor
      workspaceId={workspace.id}
      headers={fileHeader}
      previewRows={previewRows}
      formRef={columnMapFormRef}
      onConfirm={handleConfirm}
      onPrev={handlePrev}
      isConfirmDisabled={isToCheckUploadSuccess && !isUploadSuccess}
      showAddressNormalization
      confirmTooltipEnabled={isToCheckUploadSuccess && !isUploadSuccess}
      confirmTooltipContent={t('file_upload_issue')}
      requiredTraitIds={requiredMapping?.traitIds}
      requiredTraitsTooltipContent={requiredMapping?.tooltip}
      suggestedHeaders={suggestionRequest.headers}
      suggestionSources={suggestionRequest.sources}
      isSuggestionsPending={suggestionRequest.pending}
      ignoreSavedHeaders={!hasSavedMapping}
    />
  );
};
